import { useMemo } from "react";
import { currentMonth, currentYear } from "src/constants";
import { useResponseRedirect, useSectionProgress } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { useAuth } from "src/context";
import {
  fromBorrowerPersonalInfo,
  toBankruptcyUpdateReq,
  type PersonalInfoFormData,
} from "src/api/adapters/borrower";
import type { UpdateBorrowerStatusFormData } from "src/types/borrower";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import {
  FileDollarIcon,
  FormTable,
  FormTableBody,
  PiiFields,
} from "@porchpass/ui";
import {
  bankruptcyPiiErrorsAfterSubmit,
  fromBankruptcyPiiFormData,
  toBankruptcyPiiFormData,
} from "src/forms/adapters/pii";
import {
  test,
  validate,
  type ValidationMapperFunc,
  isPastOrCurrentDate,
  isPastDateBeyondLimit,
  notEmpty,
  safeNumber,
} from "src/utils";

type BankruptcyFormData = PersonalInfoFormData & {
  has_declared_bankruptcy?: boolean;
};

const validationMapper: ValidationMapperFunc<BankruptcyFormData> = (data) => ({
  bankruptcy_month: [
    test(
      (val) => (data.has_declared_bankruptcy ? notEmpty(val) : true),
      "Must select a month",
    ),
    test(
      (val) =>
        !data.bankruptcy_year
          ? true
          : !isPastDateBeyondLimit(
              safeNumber(val),
              safeNumber(data.bankruptcy_year),
              currentMonth,
              currentYear - 5,
            ),
      "Must be within 5 years",
    ),
  ],
  bankruptcy_year: [
    test(
      (val) => (data.has_declared_bankruptcy ? notEmpty(val) : true),
      "Must select a year",
    ),
    test(
      () =>
        isPastOrCurrentDate(
          data.bankruptcy_month ?? "",
          data.bankruptcy_year ?? "",
        ),
      "Date must be in the past",
    ),
  ],
});

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const sectionProgress = useSectionProgress();
  const { formData, updateField } = useFormLoad<BankruptcyFormData>(
    {},
    () => api.getBorrower(),
    (data) =>
      fromBorrowerPersonalInfo(
        data as Parameters<typeof fromBorrowerPersonalInfo>[0],
      ),
    { enabled: !!firebaseUser },
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit(async (data: BankruptcyFormData) => {
      return api.updateBorrower(toBankruptcyUpdateReq(data));
    });
  let nextPagePath = "/home";
  if (sectionProgress["expenses"] && sectionProgress["property"])
    nextPagePath = "../../progress";
  useResponseRedirect(nextPagePath, response);

  const piiValue = useMemo(
    () => toBankruptcyPiiFormData(formData as UpdateBorrowerStatusFormData),
    [formData],
  );

  const mergedFormData = useMemo(
    () => ({
      ...formData,
      ...fromBankruptcyPiiFormData(piiValue),
    }),
    [formData, piiValue],
  );

  const formErrors = validate<BankruptcyFormData>(
    mergedFormData,
    validationMapper,
  );

  const piiErrors = useMemo(
    () =>
      bankruptcyPiiErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const submitNoBankruptcy = () => {
    void submitHandler({
      bankruptcy_month: "",
      bankruptcy_year: "",
    });
  };

  const applyPiiPatch = (patch: Partial<typeof piiValue>) => {
    const mapped = fromBankruptcyPiiFormData(patch);
    for (const [key, val] of Object.entries(mapped)) {
      if (val !== undefined) {
        updateField(key as keyof BankruptcyFormData)(val);
      }
    }
  };

  let footerTitle = "";
  if (piiValue.hasBankruptcy) footerTitle = "Next";
  if (sectionProgress["personal_info"] && sectionProgress["property"])
    footerTitle = "Save";

  return (
    <FormLayout
      title="Have you filed bankruptcy in the last 5 years?"
      headerTitle="Expenses"
      stepIndex={6}
      footerTitle={footerTitle}
      clickHandler={() => void submitHandler(mergedFormData, formErrors)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Bankruptcy"
          headerIcon={
            <FileDollarIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#BC3C7C]"
            />
          }
        >
          <FormTableBody>
            <PiiFields
              value={piiValue}
              showBankruptcyOnly
              errors={piiErrors}
              onChange={applyPiiPatch}
              onBankruptcySelect={(hasBankruptcy) => {
                if (!hasBankruptcy) {
                  submitNoBankruptcy();
                }
              }}
              onBankruptcyReselect={(hasBankruptcy) => {
                if (!hasBankruptcy) {
                  submitNoBankruptcy();
                }
              }}
            />
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
